import React, { useEffect, useState } from "react";
import { Megaphone, Users, Copy, Rocket, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionBlock, Field, FieldGrid } from "./Channel";

const BOOST_SLOTS = [
  { key: "homepage_hero", label: "Homepage Hero (top banner)" },
  { key: "homepage_row",  label: "Homepage Featured Row" },
  { key: "browse_top",    label: "Browse Page — Top Slot" },
];

const STATUS_META = {
  pending:  { label: "Pending",  color: "orange" },
  approved: { label: "Approved", color: "volt" },
  rejected: { label: "Rejected", color: "pink" },
};

export default function Promotions() {
  const [ref, setRef] = useState(null);
  const [myBoosts, setMyBoosts] = useState([]);
  const [myFeatured, setMyFeatured] = useState([]);
  const [busy, setBusy] = useState(false);

  // Boost form
  const [slot, setSlot] = useState("homepage_row");
  const [scheduledFor, setScheduledFor] = useState("");
  const [boostNotes, setBoostNotes] = useState("");

  // Featured form
  const [pitch, setPitch] = useState("");
  const [category, setCategory] = useState("");
  const [twitter, setTwitter] = useState("");
  const [youtube, setYoutube] = useState("");

  const load = () => {
    api.get("/promotions/referrals").then((r) => setRef(r.data)).catch(() => {});
    api.get("/promotions/boost/mine").then((r) => setMyBoosts(r.data.requests || [])).catch(() => {});
    api.get("/promotions/featured/mine").then((r) => setMyFeatured(r.data.applications || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submitBoost = async () => {
    setBusy(true);
    try {
      await api.post("/promotions/boost", { slot, scheduled_for: scheduledFor || null, notes: boostNotes });
      toast.success("Boost request submitted");
      setScheduledFor(""); setBoostNotes("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const submitFeatured = async () => {
    if (pitch.length < 20) return toast.error("Please write a pitch (≥ 20 characters)");
    setBusy(true);
    try {
      await api.post("/promotions/featured", { pitch, category, twitter, youtube });
      toast.success("Featured application submitted");
      setPitch(""); setCategory(""); setTwitter(""); setYoutube("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!ref?.referral_link) return;
    const url = `${window.location.origin}${ref.referral_link}`;
    navigator.clipboard.writeText(url);
    toast.success("Referral link copied");
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="section-promotions">
      {/* Referral */}
      <SectionBlock icon={Users} title="Referral Program" color="cyan">
        {!ref ? (
          <div className="text-xs text-zinc-500"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <Stat label="YOUR CODE"    value={ref.referral_code} color="cyan" mono testid="promo-ref-code" />
              <Stat label="REFERRED"     value={ref.total_referred} color="volt" testid="promo-ref-total" />
              <Stat label="CONVERTED"    value={ref.converted}      color="pink" testid="promo-ref-converted" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <input
                className="sc-input flex-1 min-w-[240px] sc-mono text-xs"
                value={`${window.location.origin}${ref.referral_link}`}
                readOnly
                data-testid="promo-ref-link"
              />
              <button onClick={copyLink} className="sc-btn text-xs flex items-center gap-1.5" data-testid="promo-ref-copy">
                <Copy className="w-3.5 h-3.5" /> Copy link
              </button>
            </div>
            {ref.recent?.length > 0 && (
              <div className="mt-4">
                <div className="sc-label text-[10px] mb-2">RECENT SIGNUPS</div>
                <ul className="space-y-1">
                  {ref.recent.slice(0, 6).map((r, i) => (
                    <li key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                      <span className="text-zinc-300">{r.referred_username}</span>
                      <span className="sc-mono text-[10px] text-zinc-500">{new Date(r.created_at).toLocaleDateString()} · {r.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </SectionBlock>

      {/* Boost request */}
      <SectionBlock icon={Rocket} title="Homepage Boost" color="volt">
        <p className="text-xs text-zinc-400 -mt-1">
          Request a placement in a featured slot. Staff review and approve boosts based on availability and channel fit.
        </p>
        <FieldGrid>
          <Field label="Slot">
            <select className="sc-input" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="promo-boost-slot">
              {BOOST_SLOTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Preferred date" hint="Optional">
            <input type="date" className="sc-input" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} data-testid="promo-boost-date" />
          </Field>
          <Field label="Notes for staff" fullWidth>
            <textarea className="sc-input min-h-[70px]" value={boostNotes} onChange={(e) => setBoostNotes(e.target.value)} placeholder="What's happening? Big event, tournament, milestone stream…" data-testid="promo-boost-notes" />
          </Field>
        </FieldGrid>
        <button
          onClick={submitBoost}
          disabled={busy}
          className="sc-btn text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="promo-boost-submit"
        >
          <Rocket className="w-4 h-4" /> Submit boost request
        </button>

        {myBoosts.length > 0 && <RequestsList title="YOUR BOOSTS" rows={myBoosts.map((b) => ({
          key: b.id, left: BOOST_SLOTS.find((s) => s.key === b.slot)?.label || b.slot, right: b.status,
          sub: `${b.scheduled_for || "any date"} · ${new Date(b.created_at).toLocaleDateString()}`,
        }))} testid="promo-boost-list" />}
      </SectionBlock>

      {/* Featured application */}
      <SectionBlock icon={Star} title="Apply to be Featured" color="pink">
        <p className="text-xs text-zinc-400 -mt-1">
          Tell the team about your channel. Featured streamers get long-term homepage placement, curated collections, and cross-promotion.
        </p>
        <FieldGrid>
          <Field label="Category">
            <input className="sc-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Just Chatting, IRL, Music…" data-testid="promo-featured-category" />
          </Field>
          <Field label="X / Twitter (optional)">
            <input className="sc-input" value={twitter} onChange={(e) => setTwitter(e.target.value)} data-testid="promo-featured-twitter" />
          </Field>
          <Field label="YouTube channel (optional)">
            <input className="sc-input" value={youtube} onChange={(e) => setYoutube(e.target.value)} data-testid="promo-featured-youtube" />
          </Field>
          <Field label="Your pitch" hint="At least 20 characters" fullWidth>
            <textarea
              className="sc-input min-h-[120px]"
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              placeholder="What makes your channel special? Content style, audience, milestones…"
              data-testid="promo-featured-pitch"
            />
          </Field>
        </FieldGrid>
        <button
          onClick={submitFeatured}
          disabled={busy || pitch.length < 20}
          className="sc-btn text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="promo-featured-submit"
        >
          <Star className="w-4 h-4" /> Submit application
        </button>

        {myFeatured.length > 0 && <RequestsList title="YOUR APPLICATIONS" rows={myFeatured.map((f) => ({
          key: f.id, left: f.category || "(no category)", right: f.status,
          sub: new Date(f.created_at).toLocaleDateString(),
        }))} testid="promo-featured-list" />}
      </SectionBlock>
    </div>
  );
}

function Stat({ label, value, color, mono, testid }) {
  return (
    <div className={`term-card ${color} p-3`} data-testid={testid}>
      <div className="sc-label text-[10px]" style={{ color: `var(--sc-${color})` }}>{label}</div>
      <div className={`text-2xl font-black mt-1 ${mono ? "sc-mono" : "sc-heading"}`}>{value}</div>
    </div>
  );
}

function RequestsList({ title, rows, testid }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <div className="sc-label text-[10px] mb-2">{title}</div>
      <ul className="space-y-1">
        {rows.map((r) => {
          const meta = STATUS_META[r.right] || {};
          return (
            <li key={r.key} className="flex items-center justify-between py-2 px-3 border border-white/5 rounded">
              <div className="min-w-0">
                <div className="text-sm text-zinc-200 truncate">{r.left}</div>
                <div className="text-[10px] sc-mono text-zinc-500">{r.sub}</div>
              </div>
              <span className="text-[10px] sc-mono px-2 py-0.5 rounded font-bold" style={{ background: `var(--sc-${meta.color})22`, color: `var(--sc-${meta.color})` }}>
                {(meta.label || r.right).toUpperCase()}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
